import { toast } from '@/components/ToastNotification'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import {
  maskCEP,
  maskCNPJ,
  maskTelefone,
  unmaskCEP,
  unmaskCNPJ,
  unmaskTelefone
} from '@/lib/utils'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { EmpresaFormData } from './types'

interface DadosEmpresaTabProps {
  getIdEmpresa: () => number | null
}

const initialFormData: EmpresaFormData = {
  nome: '',
  nome_fantasia: '',
  cnpj: '',
  email: '',
  telefone: '',
  logo: '',
  endereco: {
    id_pessoa: 0,
    logradouro: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: '',
    cep: '',
    complemento: ''
  }
}

export function DadosEmpresaTab({ getIdEmpresa }: DadosEmpresaTabProps) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<EmpresaFormData>(initialFormData)
  const cepDebounceTimeout = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const loadEmpresa = async () => {
      const id = getIdEmpresa()
      if (!id) return
      setLoading(true)
      try {
        const response = await api.get(`/api/empresas/${id}`)
        const empresa = response.result
        if (!empresa) {
          toast.error('Empresa não encontrada. Faça login novamente.')
          return
        }
        setFormData({
          id: empresa.id,
          nome: empresa.nome || '',
          nome_fantasia: empresa.nome_fantasia || '',
          cnpj: empresa.cnpj || '',
          email: empresa.email || '',
          telefone: empresa.telefone || '',
          logo: empresa.logo || '',
          id_endereco: empresa.id_endereco,
          endereco: empresa.endereco
            ? {
              id: empresa.endereco.id,
              id_pessoa: empresa.endereco.id_pessoa,
              cep: empresa.endereco.cep || '',
              logradouro: empresa.endereco.logradouro || '',
              numero: empresa.endereco.numero || '',
              bairro: empresa.endereco.bairro || '',
              cidade: empresa.endereco.cidade || '',
              uf: empresa.endereco.uf || '',
              complemento: empresa.endereco.complemento || ''
            }
            : { ...initialFormData.endereco! }
        })
      } catch (error: any) {
        console.error('Erro ao carregar empresa:', error)
        toast.error(
          error?.message || 'Não foi possível carregar os dados da empresa'
        )
      } finally {
        setLoading(false)
      }
    }
    loadEmpresa()
  }, [getIdEmpresa])

  useEffect(() => {
    return () => {
      if (cepDebounceTimeout.current) {
        clearTimeout(cepDebounceTimeout.current)
      }
    }
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload: any = {
        nome: formData.nome,
        nome_fantasia: formData.nome_fantasia,
        cnpj: formData.cnpj,
        email: formData.email || undefined,
        telefone: formData.telefone || undefined,
        logo: formData.logo || undefined
      }
      if (formData.endereco?.logradouro) {
        payload.endereco = {
          id: formData.endereco.id,
          id_pessoa: formData.endereco.id_pessoa,
          cep: formData.endereco.cep,
          logradouro: formData.endereco.logradouro,
          numero: formData.endereco.numero,
          bairro: formData.endereco.bairro,
          cidade: formData.endereco.cidade,
          uf: formData.endereco.uf,
          complemento: formData.endereco.complemento
        }
      }
      if (formData.id) {
        await api.put(`/api/empresas/${formData.id}`, payload)
        toast.success('Configurações atualizadas com sucesso!')
      } else {
        const response = await api.post(`/api/empresas`, payload)
        if (response.result?.id) {
          setFormData(prev => ({ ...prev, id: response.result.id }))
        }
        toast.success('Empresa criada com sucesso!')
      }
    } catch (error: any) {
      console.error('Erro ao salvar empresa:', error)
      toast.error(error?.message || 'Não foi possível salvar as configurações')
    } finally {
      setSaving(false)
    }
  }

  const buscarCep = async (cep: string) => {
    try {
      const cepLimpo = cep.replace(/\D/g, '')
      if (cepLimpo.length !== 8) return
      const response: { result: any } = await api.get(`/api/cep/${cepLimpo}`)
      if (response.result && !response.result.erro) {
        const dadosCep = response.result
        setFormData(prev => ({
          ...prev,
          endereco: {
            ...prev.endereco!,
            id_pessoa: prev.endereco?.id_pessoa || 0,
            logradouro: dadosCep.logradouro || '',
            bairro: dadosCep.bairro || '',
            cidade: dadosCep.localidade || '',
            uf: dadosCep.uf || '',
            complemento: dadosCep.complemento || ''
          }
        }))
      } else {
        toast.warning('CEP não encontrado , verifique se o CEP está correto')
      }
    } catch (error: any) {
      console.error('Erro ao buscar CEP:', error)
      toast.error(error?.message || 'Erro ao buscar o CEP')
    }
  }

  const handleCepChange = (value: string) => {
    const cepSemMascara = unmaskCEP(value)
    setFormData({
      ...formData,
      endereco: { ...formData.endereco!, cep: cepSemMascara }
    })
    if (cepDebounceTimeout.current) clearTimeout(cepDebounceTimeout.current)
    if (cepSemMascara.length === 8) {
      cepDebounceTimeout.current = setTimeout(() => buscarCep(cepSemMascara), 500)
    }
  }

  const handleCnpjChange = (value: string) => {
    setFormData({ ...formData, cnpj: unmaskCNPJ(value) })
  }

  const handleTelefoneChange = (value: string) => {
    setFormData({ ...formData, telefone: unmaskTelefone(value) })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="logo">Logo da Empresa</Label>
            <Input
              id="logo"
              type="file"
              accept="image/*"
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) {
                  if (file.size > 2 * 1024 * 1024) {
                    toast.error('A logo deve ter no máximo 2MB')
                    e.target.value = ''
                    return
                  }
                  const reader = new FileReader()
                  reader.onloadend = () => {
                    setFormData({ ...formData, logo: reader.result as string })
                  }
                  reader.readAsDataURL(file)
                } else {
                  setFormData({ ...formData, logo: '' })
                }
              }}
            />
            {formData.logo && (
              <div className="mt-2 text-center">
                <img
                  src={formData.logo}
                  alt="Logo preview"
                  className="h-16 object-contain rounded border inline-block"
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Formatos aceitos: JPG, PNG, GIF (Máx. 2MB)
            </p>
          </div>
          <h3 className="font-semibold text-lg">Informações da Empresa</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome da Empresa *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={e => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Nome completo da empresa"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nome_fantasia">Nome Fantasia *</Label>
              <Input
                id="nome_fantasia"
                value={formData.nome_fantasia}
                onChange={e =>
                  setFormData({ ...formData, nome_fantasia: e.target.value })
                }
                placeholder="Nome fantasia"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ *</Label>
              <Input
                id="cnpj"
                value={maskCNPJ(formData.cnpj || '')}
                onChange={e => handleCnpjChange(e.target.value)}
                placeholder="00.000.000/0000-00"
                maxLength={18}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone</Label>
              <Input
                id="telefone"
                value={maskTelefone(formData.telefone || '')}
                onChange={e => handleTelefoneChange(e.target.value)}
                placeholder="(00) 00000-0000"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={e =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="email@exemplo.com"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 border-t pt-4">
          <h3 className="font-semibold text-lg">Endereço</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cep">CEP</Label>
              <Input
                id="cep"
                value={maskCEP(formData.endereco?.cep || '')}
                onChange={e => handleCepChange(e.target.value)}
                placeholder="00000-000"
                maxLength={9}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="logradouro">Logradouro *</Label>
              <Input
                id="logradouro"
                value={formData.endereco?.logradouro || ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    endereco: {
                      ...formData.endereco!,
                      logradouro: e.target.value
                    }
                  })
                }
                placeholder="Rua, Avenida, etc."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero">Número</Label>
              <Input
                id="numero"
                value={formData.endereco?.numero || ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    endereco: { ...formData.endereco!, numero: e.target.value }
                  })
                }
                placeholder="123"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="complemento">Complemento</Label>
              <Input
                id="complemento"
                value={formData.endereco?.complemento || ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    endereco: {
                      ...formData.endereco!,
                      complemento: e.target.value
                    }
                  })
                }
                placeholder="Apto, Bloco, etc."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bairro">Bairro</Label>
              <Input
                id="bairro"
                value={formData.endereco?.bairro || ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    endereco: { ...formData.endereco!, bairro: e.target.value }
                  })
                }
                placeholder="Nome do bairro"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cidade">Cidade</Label>
              <Input
                id="cidade"
                value={formData.endereco?.cidade || ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    endereco: { ...formData.endereco!, cidade: e.target.value }
                  })
                }
                placeholder="Nome da cidade"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="uf">UF</Label>
              <Input
                id="uf"
                value={formData.endereco?.uf || ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    endereco: {
                      ...formData.endereco!,
                      uf: e.target.value.toUpperCase()
                    }
                  })
                }
                placeholder="SP"
                maxLength={2}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Configurações'
            )}
          </Button>
        </div>
      </div>
    </Card>
  )
}
